import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from "vue";
import {
    LocationQueryValue,
    useRoute,
    useRouter,
} from "vue-router";
import { useLocale } from "../locales/i18n.js";
import { useFetch } from "../utils.js";
import Store, { type GuildData, type SelectedGuildAccessError } from "../store.js";

type ProfileTab = "servers" | "apps" | "integrations";

type ProfileTabValue = {
    key: ProfileTab;
    icon: string;
    labelKey: string;
};

type ProfileApplicationApiKey = {
    id: string;
    keyId: string;
    name: string;
    prefix: string;
    scopes: string[];
    lastUsedAt: string | null;
    revokedAt: string | null;
};

type ProfileApplicationLogType = {
    type: string;
    namespacedType: string;
    severity: "info" | "warning" | "danger";
    labels?: {
        fr?: string;
        en?: string;
    };
    description?: {
        fr?: string;
        en?: string;
    };
    display?: {
        fr?: string;
        en?: string;
    } | null;
    schema?: Record<string, unknown> | null;
};

type ProfileApplicationInstallation = {
    appId: string;
    guildId: string;
    status: "pending" | "active" | "disabled" | "revoked";
    enabledLogTypes: string[];
};

type ProfileApplication = {
    id: string;
    appId: string;
    name: string;
    slug: string;
    description: string;
    status: "active" | "disabled" | "deleted";
    rootKey?: ProfileApplicationApiKey | null;
    apiKeys?: ProfileApplicationApiKey[];
    logTypes?: ProfileApplicationLogType[];
    installations?: ProfileApplicationInstallation[];
    limits?: {
        eventsPerSecond: number;
        maxEventBytes: number;
        maxBatchSize: number;
        shortTokenTtlSeconds: number;
    };
};

type ProfileTemporaryToken = {
    accessToken: string;
    expiresAt: string;
};

type GuildMemberSelection = {
    guildId: string;
};

type GuildMemberSelectionResponse = {
    guildMember: GuildMemberSelection | null;
};

type GuildSelectionErrorResponse = {
    code?   : string;
    guildId?: string;
    error?  : string;
};

function getApplicationRootKey(app: ProfileApplication) {
    return app.rootKey ?? (app.apiKeys ?? []).find((key) => !key.revokedAt) ?? null;
}

const TABS: ReadonlyArray<ProfileTabValue> = [
    { key: "servers", icon: "dns", labelKey: "profile.tabs.servers" },
    { key: "apps", icon: "apps", labelKey: "profile.tabs.apps" },
    { key: "integrations", icon: "settings_ethernet", labelKey: "profile.tabs.integrations" },
] as const;

const ADMINISTRATOR_PERMISSION = 8n;
const MANAGE_GUILD_PERMISSION = 32n;

function computeTabValue(raw: string | undefined): ProfileTab {
    if (raw === "apps" || raw === "integrations")
        return raw;

    return "servers";
}

function normalizeProfileTab(raw: LocationQueryValue | LocationQueryValue[] | null | undefined): string | undefined {
    const normalized = Array.isArray(raw) ? raw[0] : raw;

    return typeof normalized === "string"
        ? normalized
        : undefined;
}

function parsePermissionBits(permission: string | undefined): bigint | null {
    if (!permission)
        return null;

    try {
        return BigInt(permission);
    } catch {
        return null;
    }
}

function formatProfileCount(value: number | null | undefined): string {
    if (value == null)
        return "—";

    return new Intl.NumberFormat("fr-FR").format(value);
}

function normalizeSearchValue(value: string): string {
    return value
        .trim()
        .toLowerCase();
}

function canInviteBot(guild: GuildData): boolean {
    if (guild.bot)
        return false;

    if (guild.owner)
        return true;

    if (guild.canInvite === false)
        return false;

    if (guild.canInvite === true)
        return true;

    const permissionBits = parsePermissionBits(guild.permissions);

    if (!permissionBits)
        return false;

    return (permissionBits & ADMINISTRATOR_PERMISSION) !== 0n
        || (permissionBits & MANAGE_GUILD_PERMISSION) !== 0n;
}

function canUseGuildForLogs(guild: GuildData): boolean {
    return Boolean(guild.bot && guild.hasAccess);
}

function canInstallApplicationOnGuild(guild: GuildData): boolean {
    return Boolean(guild.bot) && canManageProfileGuild(guild);
}

function canManageProfileGuild(guild: GuildData): boolean {
    if (guild.owner)
        return true;

    const permissionBits = parsePermissionBits(guild.permissions);

    if (!permissionBits)
        return false;

    return (permissionBits & ADMINISTRATOR_PERMISSION) !== 0n
        || (permissionBits & MANAGE_GUILD_PERMISSION) !== 0n;
}

function buildInviteUrl(botClientId: string | undefined, guildId: string): string {
    if (!botClientId)
        return "/api/oauth2";

    const inviteUrl = new URL("https://discord.com/oauth2/authorize");

    inviteUrl.searchParams.set("client_id", botClientId);
    inviteUrl.searchParams.set("scope", "bot applications.commands");
    inviteUrl.searchParams.set("permissions", "0");
    inviteUrl.searchParams.set("guild_id", guildId);
    inviteUrl.searchParams.set("disable_guild_select", "true");

    return inviteUrl.toString();
}

function buildGeneralInviteUrl(botClientId: string | undefined): string {
    if (!botClientId)
        return "/api/oauth2";

    const inviteUrl = new URL("https://discord.com/oauth2/authorize");

    inviteUrl.searchParams.set("client_id", botClientId);
    inviteUrl.searchParams.set("scope", "bot applications.commands");
    inviteUrl.searchParams.set("permissions", "0");

    return inviteUrl.toString();
}

function buildFullLoginUrl() {
    const url = new URL("/api/oauth2", window.location.origin);

    url.searchParams.set("returnTo", "/profile");

    return url.toString();
}

function formatUserAvatar(id: string, avatar: string | null): string {
    if (avatar)
        return `https://cdn.discordapp.com/avatars/${id}/${avatar}.png?size=256`;

    const fallback = Number((BigInt(id) >> 22n) % 6n);

    return `https://cdn.discordapp.com/embed/avatars/${fallback}.png`;
}

function sortProfileGuilds(guilds: GuildData[]): GuildData[] {
    return [ ...guilds ].sort((left, right) => {
        if (!!left.bot !== !!right.bot)
            return Number(!!right.bot) - Number(!!left.bot);

        if (canInviteBot(left) !== canInviteBot(right))
            return Number(canInviteBot(right)) - Number(canInviteBot(left));

        if (left.hasAccess !== right.hasAccess)
            return Number(right.hasAccess) - Number(left.hasAccess);

        if (!!left.owner !== !!right.owner)
            return Number(right.owner) - Number(left.owner);

        return left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
    });
}

export default defineComponent({
    setup() {
        const { fetch: apiFetch }    = useFetch();
        const router                = useRouter();
        const route                 = useRoute();
        const { i18n }              = useLocale();

        const currentTab        = normalizeProfileTab(route.query["tab"]);
        const refTab            = ref<ProfileTab>(computeTabValue(currentTab));
        const refShowAllGuilds  = ref(false);
        const refApps           = ref<ProfileApplication[]>([]);
        const refAppsLoading    = ref(false);
        const refAppsLoaded     = ref(false);
        const refAppsError      = ref<string | null>(null);
        const refTemporaryTokens = ref<Record<string, ProfileTemporaryToken>>({});
        const refServerSearch   = ref("");
        const refServerSearchInput = ref<HTMLInputElement | null>(null);
        const refProfileMenuOpen = ref(false);
        const refRevealedRootKeys = ref<Record<string, boolean>>({});
        const refSelectedGuildAccessError = ref<SelectedGuildAccessError | null>(null);

        watch(() => Store.me?.selectedGuildAccessError, (error) => {
            refSelectedGuildAccessError.value = error ?? null;
        }, { immediate: true });

        watch(() => route.query["tab"], (tab) => {
            refTab.value = computeTabValue(normalizeProfileTab(tab));
        });

        watch(refTab, (tab) => {
            if (tab === "apps" || tab === "integrations")
                void loadApplications();
        });

        watch(() => Store.me?.id, (userId) => {
            if (userId && !refAppsLoaded.value)
                void loadApplications();
        }, { immediate: true });

        const isConnected = computed(() => Store.me !== null);
        const userMeta = computed(() => {
            if (!Store.me)
                return null;

            return {
                avatarUrl   : formatUserAvatar(Store.me.id, Store.me.avatar),
                displayName : Store.me.globalName || Store.me.username,
                username    : Store.me.username,
            };
        });

        const selectedGuild = computed(() => Store.selectedGuild?.id ?? null);
        const hasFullOAuthSession = computed(() => Store.me?.authMode !== "handoff");
        const visibleTabs = computed(() => hasFullOAuthSession.value
            ? TABS
            : TABS.filter((tab) => tab.key === "servers"),
        );
        const sortedGuilds = computed(() => sortProfileGuilds(Store.me?.guilds ?? []));
        const workspaceName = computed(() => userMeta.value?.displayName ?? "Workspace");
        const baseVisibleGuilds = computed(() => refShowAllGuilds.value
            ? sortedGuilds.value
            : sortedGuilds.value.filter((guild) => canUseGuildForLogs(guild) || canInviteBot(guild)),
        );
        const visibleGuilds = computed(() => {
            const query = normalizeSearchValue(refServerSearch.value);

            if (!query)
                return baseVisibleGuilds.value;

            return baseVisibleGuilds.value.filter((guild) => normalizeSearchValue(guild.name).includes(query));
        });
        const hiddenWithoutAccessCount = computed(() => sortedGuilds.value.filter((guild) => !canUseGuildForLogs(guild) && !canInviteBot(guild)).length);
        const inviteableCount = computed(() => sortedGuilds.value.filter((guild) => canInviteBot(guild)).length);
        const activeGuildCount = computed(() => sortedGuilds.value.filter(canUseGuildForLogs).length);
        const activeApps = computed(() => refApps.value.filter((app) => app.status === "active"));
        const integrationRows = computed(() => refApps.value.flatMap((app) => (app.installations ?? []).map((installation) => ({
            app,
            installation,
            guild: sortedGuilds.value.find((guild) => guild.id === installation.guildId) ?? null,
        }))));
        const activeIntegrationCount = computed(() => integrationRows.value.filter((row) => row.installation.status === "active").length);

        watch(hasFullOAuthSession, (canUseDeveloperSurfaces) => {
            if (!canUseDeveloperSurfaces && refTab.value !== "servers")
                setTab("servers");
        }, { immediate: true });

        onMounted(() => {
            window.addEventListener("keydown", handleGlobalKeydown);
        });

        onUnmounted(() => {
            window.removeEventListener("keydown", handleGlobalKeydown);
        });

        function handleGlobalKeydown(event: KeyboardEvent) {
            if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "k")
                return;

            event.preventDefault();

            if (refTab.value !== "servers")
                setTab("servers");

            refServerSearchInput.value?.focus();
        }

        function setTab(tab: ProfileTab) {
            refProfileMenuOpen.value = false;

            router.replace({
                query: {
                    ...route.query,
                    tab,
                },
            });
        }

        async function signOut() {
            await apiFetch("/api/logout", { method: "POST" }).catch(() => undefined);
            Store.me = null;
            Store.selectedGuild = null;
            location.assign("/");
        }

        async function loadApplications() {
            if (!hasFullOAuthSession.value || refAppsLoading.value)
                return;

            refAppsLoading.value = true;
            refAppsError.value = null;

            try {
                const response = await apiFetch("/api/apps");

                if (!response.ok)
                    throw new Error("Unable to load applications.");

                refApps.value = await response.json() as ProfileApplication[];
                refAppsLoaded.value = true;
            } catch (error) {
                refAppsError.value = error instanceof Error ? error.message : "Unable to load applications.";
            } finally {
                refAppsLoading.value = false;
            }
        }

        async function createApplication() {
            const name = window.prompt(i18n("profile.apps.create_prompt"));

            if (!name?.trim())
                return;

            const response = await apiFetch("/api/apps", {
                method  : "POST",
                headers : {
                    "Content-Type": "application/json",
                },
                body    : JSON.stringify({ name: name.trim() }),
            });

            if (response.ok)
                await loadApplications();
        }

        async function copyRootKeyToClipboard(key: string) {
            if (!navigator.clipboard) {
                window.alert(i18n("profile.apps.root_key_generated_no_clipboard"));
                return;
            }

            try {
                await navigator.clipboard.writeText(key);
                window.alert(i18n("profile.apps.root_key_copied"));
            } catch {
                window.alert(i18n("profile.apps.root_key_generated_no_clipboard"));
            }
        }

        async function generateRootKey(app: ProfileApplication) {
            const response = await apiFetch(`/api/apps/${app.appId}/api-keys`, {
                method  : "POST",
                headers : {
                    "Content-Type": "application/json",
                },
                body    : JSON.stringify({ name: "Root key" }),
            });

            if (!response.ok)
                return;

            const payload = await response.json() as ProfileApplicationApiKey & { key?: string };

            if (payload.key)
                await copyRootKeyToClipboard(payload.key);

            await loadApplications();
        }

        async function revokeRootKey(app: ProfileApplication) {
            const activeKey = getApplicationRootKey(app);

            if (!activeKey)
                return;

            const confirmed = window.confirm(i18n("profile.apps.root_key_revoke_confirm"));

            if (!confirmed)
                return;

            const response = await apiFetch(`/api/apps/${app.appId}/api-keys/${activeKey.keyId ?? activeKey.id}`, {
                method: "DELETE",
            });

            if (response.ok) {
                await loadApplications();
            }
        }

        async function generateTemporaryToken(app: ProfileApplication) {
            const rootKey = window.prompt(i18n("profile.apps.root_key_prompt"));

            if (!rootKey)
                return;

            const response = await apiFetch("/api/apps/token", {
                method  : "POST",
                headers : {
                    "Authorization" : `Bearer ${rootKey}`,
                    "Content-Type"  : "application/json",
                },
                body    : JSON.stringify({
                    appId       : app.appId,
                    ttlSeconds  : app.limits?.shortTokenTtlSeconds ?? 900,
                }),
            });

            if (!response.ok)
                return;

            const payload = await response.json() as ProfileTemporaryToken;

            refTemporaryTokens.value = {
                ...refTemporaryTokens.value,
                [app.appId]: payload,
            };
        }

        async function upsertLogType(app: ProfileApplication, existing?: ProfileApplicationLogType) {
            const type = existing?.type ?? window.prompt(i18n("profile.apps.log_type_prompt"), "payment.refunded")?.trim();

            if (!type)
                return;

            const labelFr = window.prompt(i18n("profile.apps.log_type_label_fr"), existing?.labels?.fr || type)?.trim() || type;
            const labelEn = window.prompt(i18n("profile.apps.log_type_label_en"), existing?.labels?.en || type)?.trim() || type;
            const displayFr = window.prompt(i18n("profile.apps.log_type_display_fr"), existing?.display?.fr || `{executor} ${type}`)?.trim() || "";
            const displayEn = window.prompt(i18n("profile.apps.log_type_display_en"), existing?.display?.en || `{executor} ${type}`)?.trim() || "";

            const response = await apiFetch(`/api/apps/${app.appId}/log-types/${encodeURIComponent(type)}`, {
                method  : "PUT",
                headers : {
                    "Content-Type": "application/json",
                },
                body    : JSON.stringify({
                    type,
                    labels: {
                        fr: labelFr,
                        en: labelEn,
                    },
                    description: {
                        fr: "",
                        en: "",
                    },
                    display: {
                        fr: displayFr,
                        en: displayEn,
                    },
                    severity: "info",
                }),
            });

            if (response.ok)
                await loadApplications();
        }

        async function deleteLogType(app: ProfileApplication, logType: ProfileApplicationLogType) {
            const confirmed = window.confirm(i18n("profile.apps.delete_log_type_confirm"));

            if (!confirmed)
                return;

            const response = await apiFetch(`/api/apps/${app.appId}/log-types/${encodeURIComponent(logType.type)}`, {
                method: "DELETE",
            });

            if (response.ok) {
                await loadApplications();
                return;
            }

            if (response.status === 409) {
                const payload = await response.json().catch(() => null) as { error?: string } | null;

                window.alert(payload?.error || i18n("profile.apps.delete_log_type_used"));
            }
        }

        async function installApplication(app: ProfileApplication) {
            const eligibleGuilds = sortedGuilds.value.filter(canInstallApplicationOnGuild);

            if (!eligibleGuilds.length) {
                window.alert(i18n("profile.apps.no_installable_guild"));
                return;
            }

            const defaultGuild = eligibleGuilds[0]?.id ?? "";
            const guildId = window.prompt(i18n("profile.apps.install_prompt"), defaultGuild)?.trim();

            if (!guildId)
                return;

            const guild = eligibleGuilds.find((candidate) => candidate.id === guildId);

            if (!guild) {
                window.alert(i18n("profile.apps.install_invalid_guild"));
                return;
            }

            const response = await apiFetch(`/api/apps/${app.appId}/installations`, {
                method  : "PUT",
                headers : {
                    "Content-Type": "application/json",
                },
                body    : JSON.stringify({
                    guildId,
                    status          : "active",
                    enabledLogTypes : (app.logTypes ?? []).map((logType) => logType.type),
                }),
            });

            if (response.ok)
                await loadApplications();
        }

        async function selectGuild(event: Event, guildId: string) {
            event.preventDefault();
            refSelectedGuildAccessError.value = null;

            const response = await apiFetch("/api/@me", {
                method  : "PATCH",
                headers : {
                    "Content-Type": "application/json",
                },
                body    : JSON.stringify({
                    selectedGuildId: guildId,
                }),
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => null) as GuildSelectionErrorResponse | null;

                if (payload?.code === "LOG_ACCESS_DENIED") {
                    refSelectedGuildAccessError.value = {
                        code    : payload.code,
                        guildId : payload.guildId ?? guildId,
                        message : payload.error ?? i18n("profile.servers.access_error_description", readGuildName(guildId)),
                    };
                }

                return;
            }

            const { guildMember } = await response.json() as GuildMemberSelectionResponse;

            if (!guildMember) {
                refSelectedGuildAccessError.value = {
                    code    : "LOG_ACCESS_DENIED",
                    guildId,
                    message : i18n("profile.servers.access_error_description", readGuildName(guildId)),
                };
                return;
            }

            Store.me!.guildMember = guildMember;
            Store.me!.selectedGuildAccessError = undefined;
            Store.selectedGuild   = Store.me!.guilds.find((guild) => guild.id === guildMember.guildId) || null;
            router.push("/");
        }

        function readGuildName(guildId: string) {
            return Store.me?.guilds.find((guild) => guild.id === guildId)?.name ?? guildId;
        }

        function openServerManager() {
            refProfileMenuOpen.value = false;
            router.push("/settings");
        }

        function toggleRootKeyReveal(appId: string) {
            refRevealedRootKeys.value = {
                ...refRevealedRootKeys.value,
                [appId]: !refRevealedRootKeys.value[appId],
            };
        }

        function renderRootKeyValue(app: ProfileApplication, rootKey: ProfileApplicationApiKey | null) {
            if (!rootKey)
                return i18n("profile.apps.no_root_key");

            if (refRevealedRootKeys.value[app.appId])
                return rootKey.prefix;

            return "••••••••••••••••••";
        }

        function renderMetric(icon: string, label: string, value: string | number) {
            return (
                <article class="profile-metric">
                    <i class="material-symbols-outlined">{icon}</i>
                    <div>
                        <strong>{value}</strong>
                        <span>{label}</span>
                    </div>
                </article>
            );
        }

        function renderSessionNotice() {
            if (hasFullOAuthSession.value)
                return null;

            return (
                <div class="profile-session-notice">
                    <div class="profile-session-notice-icon">
                        <i class="material-symbols-outlined">visibility</i>
                    </div>

                    <div class="profile-session-notice-copy">
                        <strong>{i18n("profile.session.handoff_title")}</strong>
                        <span>{i18n("profile.session.handoff_description")}</span>
                    </div>

                    <a class="profile-action-button profile-action-button--secondary" href={buildFullLoginUrl()}>
                        <i class="material-symbols-outlined">login</i>
                        {i18n("profile.session.authenticate")}
                    </a>
                </div>
            );
        }

        function readTabCount(tab: ProfileTab) {
            if (tab === "servers")
                return activeGuildCount.value;

            if (tab === "apps")
                return activeApps.value.length;

            return activeIntegrationCount.value;
        }

        function renderGuilds() {
            return (
                <>
                    <div class="profile-page-section-title">
                        <div>
                            <h2>{i18n("profile.servers.title")}</h2>
                            <small>{i18n("profile.servers.description", inviteableCount.value)}</small>
                        </div>
                    </div>

                    <div class="profile-server-toolbar">
                        <label class="profile-server-search">
                            <i class="material-symbols-outlined">search</i>
                            <input
                                ref={refServerSearchInput}
                                value={refServerSearch.value}
                                onInput={(event) => {
                                    refServerSearch.value = (event.target as HTMLInputElement).value;
                                }}
                                type="search"
                                placeholder={i18n("profile.servers.search_placeholder")}
                            />
                            <kbd>Ctrl K</kbd>
                        </label>

                        <div class="profile-server-filters">
                            <button
                                class="profile-filter-button"
                                onClick={() => {
                                    refShowAllGuilds.value = !refShowAllGuilds.value;
                                }}
                                type="button"
                            >
                                <i class="material-symbols-outlined">filter_list</i>
                                {refShowAllGuilds.value
                                    ? i18n("profile.servers.hide_inaccessible")
                                    : i18n("profile.servers.show_inaccessible")}
                            </button>

                            <span class="profile-server-count">
                                {visibleGuilds.value.length}
                                /
                                {baseVisibleGuilds.value.length}
                                {hiddenWithoutAccessCount.value > 0 && !refShowAllGuilds.value
                                    ? ` (${i18n("profile.servers.hidden_count", hiddenWithoutAccessCount.value)})`
                                    : ""}
                            </span>
                        </div>
                    </div>

                    {sortedGuilds.value.length === 0 && (
                        <div class="profile-empty">{i18n("profile.servers.empty")}</div>
                    )}

                    {sortedGuilds.value.length > 0 && visibleGuilds.value.length === 0 && (
                        <div class="profile-product-empty">
                            <i class="material-symbols-outlined">search_off</i>
                            <h3>{i18n("profile.servers.search_empty_title")}</h3>
                            <p>{i18n("profile.servers.search_empty_description")}</p>
                        </div>
                    )}

                    {refSelectedGuildAccessError.value && (
                        <div class="profile-empty">
                            <strong>{i18n("profile.servers.access_error_title")}</strong>
                            <span>
                                {i18n(
                                    "profile.servers.access_error_description",
                                    readGuildName(refSelectedGuildAccessError.value.guildId),
                                )}
                            </span>
                        </div>
                    )}

                    <div class="profile-guild-list">
                        {visibleGuilds.value.map((guild) => {
                            const canInvite = canInviteBot(guild);
                            const canUseLogs = canUseGuildForLogs(guild);
                            const avatarUrl = guild.icon
                                ? `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png?size=256`
                                : "https://cdn.discordapp.com/embed/avatars/0.png";

                            return (
                                <article class={{
                                    "profile-guild-item": true,
                                    active : selectedGuild.value === guild.id,
                                    "profile-guild-item--inaccessible": guild.bot && !canUseLogs,
                                }} key={guild.id}>
                                    <div class="profile-guild-item-main">
                                        <img class="profile-guild-avatar" src={avatarUrl} alt={guild.name} />

                                        <div class="profile-guild-item-copy">
                                            <strong>{guild.name}</strong>

                                            <div class="profile-guild-status-row">
                                                {guild.bot ? (
                                                    <>
                                                        <span class="profile-guild-status profile-guild-status--success">
                                                            <i class="material-symbols-outlined">verified_user</i>
                                                            {i18n("profile.servers.bot_installed")}
                                                        </span>

                                                        {canUseLogs ? (
                                                            <span class="profile-guild-status profile-guild-status--inviteable">
                                                                <i class="material-symbols-outlined">admin_panel_settings</i>
                                                                {i18n("profile.servers.can_select")}
                                                            </span>
                                                        ) : (
                                                            <span class="profile-guild-status profile-guild-status--muted">
                                                                <i class="material-symbols-outlined">gpp_bad</i>
                                                                {i18n("profile.servers.no_log_access")}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : canInvite ? (
                                                    <span class="profile-guild-status profile-guild-status--inviteable">
                                                        <i class="material-symbols-outlined">add_link</i>
                                                        {i18n("profile.servers.can_invite")}
                                                    </span>
                                                ) : (
                                                    <span class="profile-guild-status profile-guild-status--muted">
                                                        <i class="material-symbols-outlined">person_off</i>
                                                        {i18n("profile.servers.bot_missing")}
                                                    </span>
                                                )}

                                                <span class="profile-guild-status profile-guild-metric">
                                                    <i class="material-symbols-outlined">groups</i>
                                                    <strong>{formatProfileCount(guild.memberCount)}</strong>
                                                    {i18n("profile.servers.members")}
                                                </span>

                                                <span class="profile-guild-status profile-guild-metric">
                                                    <i class="material-symbols-outlined">radio_button_checked</i>
                                                    <strong>{formatProfileCount(guild.approximateOnlineCount)}</strong>
                                                    {i18n("profile.servers.online")}
                                                </span>

                                                {guild.owner && (
                                                    <span class="profile-guild-status profile-guild-status--owner">
                                                        <i class="material-symbols-outlined">crown</i>
                                                        {i18n("profile.guild.owner")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {guild.bot ? (
                                        canUseLogs ? (
                                            <button
                                                class="profile-guild-action"
                                                onClick={(event) => selectGuild(event, guild.id)}
                                                disabled={selectedGuild.value === guild.id}
                                                type="button"
                                            >
                                                {selectedGuild.value === guild.id
                                                    ? i18n("profile.servers.selected")
                                                    : i18n("profile.servers.select")}
                                            </button>
                                        ) : (
                                            <span class="profile-guild-action profile-guild-action-disabled profile-guild-action--blocked">
                                                {i18n("profile.servers.no_access")}
                                            </span>
                                        )
                                    ) : canInvite ? (
                                        <a
                                            class="profile-guild-action profile-guild-action--invite"
                                            href={buildInviteUrl(Store.me?.botClientId, guild.id)}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {i18n("profile.servers.invite")}
                                        </a>
                                    ) : (
                                        <span class="profile-guild-action profile-guild-action-disabled">
                                            {i18n("profile.servers.bot_missing")}
                                        </span>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </>
            );
        }

        function renderApplications() {
            return (
                <>
                    <div class="profile-page-section-title">
                        <div>
                            <h2>{i18n("profile.apps.title")}</h2>
                            <small>{i18n("profile.apps.subtitle")}</small>
                        </div>

                        <button class="profile-action-button" onClick={createApplication} type="button">
                            <i class="material-symbols-outlined">add</i>
                            {i18n("profile.apps.create")}
                        </button>
                    </div>

                    {refAppsLoading.value && <div class="profile-empty">{i18n("modals.loading.loading_text")}</div>}
                    {refAppsError.value && <div class="profile-empty">{refAppsError.value}</div>}

                    {!refAppsLoading.value && !refAppsError.value && refApps.value.length === 0 && (
                        <div class="profile-product-empty">
                            <i class="material-symbols-outlined">apps</i>
                            <h3>{i18n("profile.apps.empty_title")}</h3>
                            <p>{i18n("profile.apps.empty_description")}</p>
                            <button class="profile-action-button" onClick={createApplication} type="button">
                                <i class="material-symbols-outlined">add</i>
                                {i18n("profile.apps.create")}
                            </button>
                        </div>
                    )}

                    <div class="profile-app-grid">
                        {refApps.value.map((app) => {
                            const activeRootKey = getApplicationRootKey(app);
                            const temporaryToken = refTemporaryTokens.value[app.appId];

                            return (
                                <article class="profile-app-card" key={app.appId}>
                                    <div class="profile-app-card-header">
                                        <div>
                                            <strong>{app.name}</strong>
                                            <span>{app.appId}</span>
                                        </div>

                                        <span class={{
                                            "profile-app-status": true,
                                            "profile-app-status--active": app.status === "active",
                                        }}>
                                            {app.status}
                                        </span>
                                    </div>

                                    <p>{app.description || i18n("profile.apps.default_description")}</p>

                                    <div class="profile-app-metrics">
                                        <span>
                                            <i class="material-symbols-outlined">speed</i>
                                            {app.limits?.eventsPerSecond ?? 20}
                                            {"/s"}
                                        </span>
                                        <span>
                                            <i class="material-symbols-outlined">schema</i>
                                            {app.logTypes?.length ?? 0}
                                            {" "}
                                            {i18n("profile.apps.log_types")}
                                        </span>
                                        <span>
                                            <i class="material-symbols-outlined">hub</i>
                                            {app.installations?.filter((installation) => installation.status === "active").length ?? 0}
                                            {" "}
                                            {i18n("profile.apps.installs")}
                                        </span>
                                    </div>

                                    <div class="profile-app-detail-block">
                                        <div class="profile-app-detail-title">
                                            <i class="material-symbols-outlined">key</i>
                                            <span>{i18n("profile.apps.root_key")}</span>
                                        </div>

                                        <div class="profile-app-secret-row">
                                            <code>{renderRootKeyValue(app, activeRootKey)}</code>
                                            {activeRootKey && (
                                                <button class="profile-mini-button profile-mini-button--quiet" onClick={() => toggleRootKeyReveal(app.appId)} type="button">
                                                    <i class="material-symbols-outlined">{refRevealedRootKeys.value[app.appId] ? "visibility_off" : "visibility"}</i>
                                                    {refRevealedRootKeys.value[app.appId]
                                                        ? i18n("profile.apps.hide_root_key")
                                                        : i18n("profile.apps.reveal_root_key")}
                                                </button>
                                            )}
                                            <button class="profile-mini-button" onClick={() => generateRootKey(app)} type="button">
                                                <i class="material-symbols-outlined">{activeRootKey ? "sync" : "add"}</i>
                                                {activeRootKey ? i18n("profile.apps.rotate_root_key") : i18n("profile.apps.generate_root_key")}
                                            </button>
                                            {activeRootKey && (
                                                <button class="profile-mini-button profile-mini-button--danger" onClick={() => revokeRootKey(app)} type="button">
                                                    <i class="material-symbols-outlined">block</i>
                                                    {i18n("profile.apps.revoke")}
                                                </button>
                                            )}
                                        </div>

                                        <small class="profile-app-token-expiry">
                                            {i18n("profile.apps.root_key_storage_notice")}
                                            {" "}
                                            {activeRootKey?.lastUsedAt
                                                ? i18n("profile.apps.last_used_at", new Date(activeRootKey.lastUsedAt).toLocaleString())
                                                : i18n("profile.apps.last_used_never")}
                                        </small>
                                    </div>

                                    <div class="profile-app-detail-block">
                                        <div class="profile-app-detail-title">
                                            <i class="material-symbols-outlined">vpn_key</i>
                                            <span>{i18n("profile.apps.temp_token")}</span>
                                        </div>

                                        <div class="profile-app-secret-row">
                                            <code>{temporaryToken?.accessToken ?? i18n("profile.apps.no_temp_token")}</code>
                                            <button class="profile-mini-button" onClick={() => generateTemporaryToken(app)} type="button">
                                                <i class="material-symbols-outlined">bolt</i>
                                                {i18n("profile.apps.generate_temp_token")}
                                            </button>
                                        </div>

                                        {temporaryToken && (
                                            <small class="profile-app-token-expiry">
                                                {i18n("profile.apps.expires_at")}
                                                {" "}
                                                {new Date(temporaryToken.expiresAt).toLocaleString()}
                                            </small>
                                        )}
                                    </div>

                                    <div class="profile-app-actions">
                                        <button class="profile-mini-button" onClick={() => upsertLogType(app)} type="button">
                                            <i class="material-symbols-outlined">library_add</i>
                                            {i18n("profile.apps.add_log_type")}
                                        </button>
                                        <button class="profile-mini-button" onClick={() => installApplication(app)} type="button">
                                            <i class="material-symbols-outlined">add_link</i>
                                            {i18n("profile.apps.install")}
                                        </button>
                                    </div>

                                    <div class="profile-app-type-list">
                                        {(app.logTypes ?? []).slice(0, 6).map((logType) => (
                                            <span class="profile-app-type-item" key={logType.namespacedType}>
                                                <code title={logType.labels?.fr || logType.labels?.en || logType.type}>
                                                    {logType.namespacedType}
                                                </code>
                                                <button class="profile-icon-button" onClick={() => upsertLogType(app, logType)} type="button" title={i18n("profile.apps.edit_log_type")}>
                                                    <i class="material-symbols-outlined">edit</i>
                                                </button>
                                                <button class="profile-icon-button profile-icon-button--danger" onClick={() => deleteLogType(app, logType)} type="button" title={i18n("profile.apps.delete_log_type")}>
                                                    <i class="material-symbols-outlined">delete</i>
                                                </button>
                                            </span>
                                        ))}
                                        {(app.logTypes?.length ?? 0) === 0 && <span>{i18n("profile.apps.no_log_types")}</span>}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </>
            );
        }

        function renderIntegrations() {
            return (
                <>
                    <div class="profile-page-section-title">
                        <div>
                            <h2>{i18n("profile.integrations.title")}</h2>
                            <small>{i18n("profile.integrations.subtitle")}</small>
                        </div>
                    </div>

                    {refAppsLoading.value && <div class="profile-empty">{i18n("modals.loading.loading_text")}</div>}
                    {refAppsError.value && <div class="profile-empty">{refAppsError.value}</div>}

                    {!refAppsLoading.value && !refAppsError.value && integrationRows.value.length === 0 && (
                        <div class="profile-product-empty">
                            <i class="material-symbols-outlined">settings_ethernet</i>
                            <h3>{i18n("profile.integrations.empty_title")}</h3>
                            <p>{i18n("profile.integrations.empty_description")}</p>
                        </div>
                    )}

                    <div class="profile-integration-list">
                        {integrationRows.value.map(({ app, installation, guild }) => (
                            <article class="profile-integration-item" key={`${app.appId}:${installation.guildId}`}>
                                <div>
                                    <strong>{app.name}</strong>
                                    <span>{guild?.name ?? installation.guildId}</span>
                                </div>

                                <div class="profile-integration-meta">
                                    <span class={{
                                        "profile-app-status": true,
                                        "profile-app-status--active": installation.status === "active",
                                    }}>
                                        {installation.status}
                                    </span>
                                    <small>
                                        {installation.enabledLogTypes.length || (app.logTypes?.length ?? 0)}
                                        {" "}
                                        {i18n("profile.integrations.enabled_types")}
                                    </small>
                                </div>
                            </article>
                        ))}
                    </div>
                </>
            );
        }

        return () => {
            if (!isConnected.value || !userMeta.value)
                return <div class="profile-page-loading">{i18n("modals.loading.loading_text")}</div>;

            return (
                <div class="profile-page-shell">
                    <div class="profile-page-card">
                        <header class="profile-page-header">
                            <div class="profile-workspace-heading">
                                <span class="profile-workspace-eyebrow">{i18n("profile.workspace.label")}</span>
                                <div class="profile-workspace-name">{workspaceName.value}</div>
                                <span class="profile-workspace-subtitle">{i18n("profile.workspace.subtitle")}</span>
                            </div>

                            <div class="profile-page-main-actions">
                                {Store.me?.botClientId && (
                                    <a class="profile-action-button profile-action-button--light" href={buildGeneralInviteUrl(Store.me.botClientId)} target="_blank" rel="noreferrer">
                                        <i class="material-symbols-outlined">add</i>
                                        {i18n("profile.servers.add_bot")}
                                    </a>
                                )}

                                <div class="profile-account-menu">
                                    <button
                                        class="profile-account-button"
                                        type="button"
                                        onClick={() => {
                                            refProfileMenuOpen.value = !refProfileMenuOpen.value;
                                        }}
                                    >
                                        <img class="profile-page-avatar" src={userMeta.value.avatarUrl} alt={userMeta.value.displayName} />
                                        <span>
                                            <strong>{userMeta.value.displayName}</strong>
                                            <small>@{userMeta.value.username}</small>
                                        </span>
                                        <i class="material-symbols-outlined">expand_more</i>
                                    </button>

                                    {refProfileMenuOpen.value && (
                                        <div class="profile-account-dropdown">
                                            <button onClick={openServerManager} type="button">
                                                <i class="material-symbols-outlined">settings</i>
                                                {i18n("profile.account.settings")}
                                            </button>
                                            <button onClick={signOut} type="button">
                                                <i class="material-symbols-outlined">logout</i>
                                                {i18n("profile.account.sign_out")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </header>

                        {renderSessionNotice()}

                        <div class="profile-overview-strip">
                            {renderMetric("dns", i18n("profile.metrics.active_servers"), activeGuildCount.value)}
                            {renderMetric("apps", i18n("profile.metrics.apps"), activeApps.value.length)}
                            {renderMetric("hub", i18n("profile.metrics.integrations"), activeIntegrationCount.value)}
                        </div>

                        <nav class="profile-page-tabs">
                            {visibleTabs.value.map((tab) => (
                                <button
                                    class={{
                                        "profile-page-tab": true,
                                        active   : refTab.value === tab.key,
                                    }}
                                    onClick={() => setTab(tab.key)}
                                    type="button"
                                    key={tab.key}
                                    >
                                        <i class="material-symbols-outlined">{tab.icon}</i>
                                        {i18n(tab.labelKey)}
                                        <span class="profile-tab-count">{readTabCount(tab.key)}</span>
                                    </button>
                                ))}
                        </nav>

                        <div class="profile-page-content">
                            {refTab.value === "servers" && renderGuilds()}
                            {refTab.value === "apps" && renderApplications()}
                            {refTab.value === "integrations" && renderIntegrations()}
                        </div>
                    </div>
                </div>
            );
        };
    },
});
